import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import StructuredData, { breadcrumbSchema } from "../components/StructuredData";

const SITE = "https://makeupbrushbasics.com";

export default function Blog() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/blog")
      .then((r) => setPosts(r.data.posts))
      .catch(() => setError("Couldn't load posts."));
  }, []);

  const blogListSchema = posts && {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "The BrushBasics Journal",
    url: `${SITE}/blog`,
    blogPost: posts.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      description: p.excerpt,
      datePublished: p.published,
      author: { "@type": "Organization", name: p.author },
      url: `${SITE}/blog/${p.slug}`,
    })),
  };

  return (
    <div data-testid="blog-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <SEO
        title="The Journal — Editorial Reads on Makeup Brush Technique"
        description="Long-form editorial writing on makeup brush technique, care, and comparison. From BrushBasics — the illustrated guide to every brush in your kit."
        path="/blog"
      />
      <StructuredData
        schemas={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "The Journal", path: "/blog" },
          ]),
          blogListSchema,
        ]}
      />

      <header className="mb-12 lg:mb-16 max-w-3xl">
        <div className="overline mb-3">Volume 04 · The Journal</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.02] text-stone-900 tracking-tight">
          Slow reads on
          <br />
          <em className="italic text-stone-700">brushwork.</em>
        </h1>
        <p className="mt-7 text-base sm:text-lg leading-relaxed text-stone-600 font-light max-w-xl">
          Considered essays on brush technique, care, and comparison. Best paired with morning coffee and unhurried curiosity.
        </p>
      </header>

      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 mb-6">
          {error}
        </div>
      )}

      {!posts && !error && (
        <p className="text-sm text-stone-500">Loading posts…</p>
      )}

      {posts && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {posts.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              data-testid={`blog-card-${post.slug}`}
              className="group block bb-card-hover"
            >
              <div className="aspect-[4/5] overflow-hidden bg-[#F2EFEB] border border-[#E5E0D8]">
                <img
                  src={post.cover_image}
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                />
              </div>
              <div className="mt-5">
                <div className="overline mb-2">
                  {new Date(post.published).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}
                  {" · "}
                  {post.reading_min} min read
                </div>
                <h2 className="font-serif-display text-2xl text-stone-900 leading-snug group-hover:text-[#8C6A5D] transition-colors">
                  {post.title}
                </h2>
                <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                  {post.excerpt}
                </p>
                <div className="mt-4 text-xs uppercase tracking-[0.22em] text-[#8C6A5D]">
                  Read the essay →
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
